import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";

import Header from "Header";
import { useLocal } from "hooks";
import {
  useGetCompareResponseQuery,
  useGetExamExtraNumQuestionsQuery,
  useGetExamNumAnswersQuery,
  useGetExamNumQuestionsQuery,
  useGetExamNumTemplatesQuery,
  useGetExamResponseQuery,
} from "store/api";
import type { GradedResponse } from "store/type";

import Option from "./Option";
import { answerColorMap, QUESTIONS_PER_BLOCK } from "./Compare.constants";
import { Key, LETTER_TO_NUM, Paths } from "../constants";

import "App.css";
import "./Compare.css";

enum Origin {
  OFFICIAL = "Plantilla Ministerio Sanidad",
  ADMIN = "Plantilla GMB",
  ALL_USERS = "Respuesta más contestada",
}

const Compare = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const userLocal = useLocal(Key.USER_ID);
  const userId = userLocal.getItem();

  const { data: api, error: compareAnswersApiError } =
    useGetCompareResponseQuery(userId, {
      skip: userId == null || typeof userId !== "string",
      refetchOnMountOrArgChange: true,
    });

  const { data: examResponse } = useGetExamResponseQuery(userId, {
    skip: userId == null || typeof userId !== "string",
  });

  const { data: numAnwerOptions = 0, error: numAnswerOptionsError } =
    useGetExamNumAnswersQuery("_", {
      skip: userId == null || typeof userId !== "string",
    });

  const { data: numAnswersSheets } = useGetExamNumTemplatesQuery(userId, {
    skip: userId == null || typeof userId !== "string",
  });

  const { data: questions = 0 } = useGetExamNumQuestionsQuery("_", {
    skip: userId == null || typeof userId !== "string",
  });

  const { data: extraQuestions = 0 } = useGetExamExtraNumQuestionsQuery("_", {
    skip: userId == null || typeof userId !== "string",
  });

  useEffect(() => {
    if (api == undefined) return;

    if (questions !== api.graded_responses.length) {
      enqueueSnackbar(
        "Number of questions don't match with number of answers set. Please contact admin.",
        { variant: "warning" }
      );
    }
  }, [questions, api?.graded_responses.length]);

  useEffect(() => {
    if (userId == null) {
      navigate(Paths.SIGN_IN);
    }
  }, []);

  const getErrorHandler = (err: any, additionalMessage = "") => {
    let message = additionalMessage;
    // @ts-ignore
    if (compareAnswersApiError.status === "FETCH_ERROR") {
      message += "No se puede alcanzar el servidor backend.";
      // @ts-ignore
    } else if (compareAnswersApiError.data?.detail != null) {
      // @ts-ignore
      message += `Error: ${compareAnswersApiError.data.detail}`;
    } else {
      message = "Por favor, inténtalo más tarde.";
    }
    enqueueSnackbar(message, {
      variant: "error",
    });
  };

  useEffect(() => {
    if (compareAnswersApiError) {
      getErrorHandler(
        compareAnswersApiError,
        "Error al obtener los datos de comparación. "
      );
    }
  }, [compareAnswersApiError]);

  useEffect(() => {
    if (numAnswerOptionsError) {
      getErrorHandler(
        compareAnswersApiError,
        "Error al obtener el número de opciones. "
      );
    }
  }, [numAnswerOptionsError]);

  const splitAnswer = () => {
    if (api == null) {
      return [[]];
    }
    const results: GradedResponse[][] = [];
    let index = 0;
    const answersToMap = Math.min(
      api.graded_responses.length,
      questions - extraQuestions
    );
    while (index < answersToMap) {
      const remaining = Math.min(answersToMap - index, QUESTIONS_PER_BLOCK);
      const gradeBlock = api.graded_responses.slice(index, index + remaining);
      results.push(gradeBlock);
      index += remaining;
    }
    while (index < api.graded_responses.length) {
      results.push(
        api.graded_responses.slice(index, index + QUESTIONS_PER_BLOCK)
      );
      index += QUESTIONS_PER_BLOCK;
    }
    return results;
  };

  const tableData = {
    Alias: examResponse?.user_name ?? "-",
    ID: userId ?? "-",
    "Opositores registrados": numAnswersSheets ?? "-",
    "Plantilla de referencia": api?.origin
      ? Origin[api.origin as keyof typeof Origin] ?? api.origin ?? "-"
      : "-",
    Correctas: api?.total_correct_answers ?? "-",
    Fallos: api?.total_incorrect_answers ?? "-",
    "No contestadas": api?.total_unanswered ?? "-",
    "ESTIMACIÓN DE RESULTADO": api?.score.toFixed(2) ?? "-",
  };

  return (
    <div className="compare-container">
      <Header
        userIdentifier={examResponse?.user_name ?? "-"}
        showEdit={true}
        showUserName={true}
        showSignOut={true}
      />
      {compareAnswersApiError != null && (
        <p>Error al recuperar las respuestas comparadas del examen</p>
      )}
      {api != null && (
        <div className="compare-content">
          <div className="user-info-container">
            <div className="text-align-end">
              {Object.keys(tableData).map((d) => {
                let className = "user-info-columns";
                if (d === "Correctas") className += " green-background";
                if (d === "Fallos") className += " red-background";
                return (
                  <div key={d} className={className}>
                    {d}
                  </div>
                );
              })}
            </div>
            <div>
              {Object.entries(tableData).map(([key, d]) => {
                let className = "user-info-columns";
                if (key === "Correctas") className += " green-background";
                if (key === "Fallos") className += " red-background";
                return (
                  <div key={`${key}-${d}`} className={className}>
                    {d}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="compare-block-container">
            {splitAnswer().map((batch, pindex) => {
              return (
                <div
                  key={pindex}
                  className="compare-block"
                  style={{
                    opacity:
                      batch[0].question_number > questions - extraQuestions
                        ? 0.7
                        : 1,
                  }}
                >
                  <div className="center compare-row">
                    <Option value={"##"} />
                    <div className="center compare-row-header-border">
                      <Option value={""} />
                      {Object.keys(LETTER_TO_NUM)
                        .slice(0, numAnwerOptions)
                        .map((key, index) => (
                          <Option
                            key={key}
                            value={key}
                            rightMostOption={index === numAnwerOptions - 1}
                          />
                        ))}
                    </div>
                  </div>
                  {batch.map((res, index) => {
                    const answerColor: "null" | "true" | "false" =
                      res.user_answer === null
                        ? "null"
                        : `${res.user_answer === res.correct_answer}`;
                    const questionBGColor = answerColorMap[answerColor];
                    return (
                      <div
                        className="center compare-row"
                        key={`${pindex} ${index}`}
                      >
                        <Option value={res.total_users_answered} />
                        <div className="center compare-row-body-border">
                          <Option
                            value={res.question_number}
                            backgroundColorClass={questionBGColor}
                            classNames={["bold"]}
                          />
                          {Object.values(LETTER_TO_NUM)
                            .slice(0, numAnwerOptions)
                            .map((num) => {
                              const optionBGColor =
                                num === res.user_answer ? questionBGColor : "";
                              const percent = res.percentage_per_answer[num];
                              const percentPreceision = Number.isInteger(
                                percent
                              )
                                ? percent
                                : percent.toFixed(1);
                              return (
                                <React.Fragment key={num}>
                                  <Option
                                    value={`${percentPreceision}%`}
                                    backgroundColorClass={optionBGColor}
                                    rightMostOption={num === numAnwerOptions}
                                  />
                                </React.Fragment>
                              );
                            })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Compare;
